import {useIntl, defineMessages} from 'react-intl'

const messages = defineMessages({
    orderNumberPlaceholder: {
        defaultMessage: 'Order Number*',
        id: 'use_order_status_fields.placeholder.order_number'
    },
    orderEmailPlaceholder: {
        defaultMessage: 'Order Email*',
        id: 'use_order_status_fields.placeholder.order_email'
    },
    billingPostcodePlaceholder: {
        defaultMessage: 'Billing Postal Code (E.g. A9A 9A9)*',
        id: 'use_order_status_fields.placeholder.billing_postcode'
    },
    orderNumberRequired: {
        defaultMessage: 'Enter your order number',
        id: 'use_order_status_fields.error.order_number_required'
    },
    emailRequired: {
        defaultMessage: 'Enter the email address associated with the order',
        id: 'use_order_status_fields.error.email_required'
    },
    emailInvalid: {
        defaultMessage: 'Enter a valid email',
        id: 'use_order_status_fields.error.email_invalid'
    },
    emailMaxLength: {
        defaultMessage: 'Email address must be 50 characters or less',
        id: 'use_order_status_fields.error.email_max_length'
    },
    postcodeRequired: {
        defaultMessage: 'Enter a valid postcode',
        id: 'use_order_status_fields.error.postcode_required'
    },
    postcodeInvalid: {
        defaultMessage: 'Enter a valid postcode',
        id: 'use_order_status_fields.error.postcode_invalid'
    }
})

/**
 * A React hook that provides the field definitions for an order status lookup form.
 * @param {Object} form - The object returned from `useForm`
 * @param {Object} form.control - The form control object
 * @param {Object} form.formState.errors - An object containing field errors
 * @returns {Object} Field definitions for use in a form
 */
export default function useOrderStatusFields({
    form: {
        control,
        formState: {errors}
    }
}) {
    const {formatMessage} = useIntl()

    const fields = {
        orderNumber: {
            name: 'orderNumber',
            type: 'text',
            autoComplete: 'off',
            rules: {
                required: formatMessage(messages.orderNumberRequired)
            },
            error: errors.orderNumber,
            control,
            label: formatMessage(messages.orderNumberPlaceholder),
            inputProps: {
                'aria-label': 'Order Number',
                'aria-required': 'true',
                'aria-invalid': errors.orderNumber ? 'true' : 'false'
            },
            variant: 'floating'
        },
        orderEmail: {
            name: 'orderEmail',
            type: 'email',
            autoComplete: 'email',
            rules: {
                required: formatMessage(messages.emailRequired),
                pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: formatMessage(messages.emailInvalid)
                },
                maxLength: {
                    value: 50,
                    message: formatMessage(messages.emailMaxLength)
                }
            },
            error: errors.orderEmail,
            control,
            label: formatMessage(messages.orderEmailPlaceholder),
            variant: 'floating',
            inputProps: {
                'aria-label': 'Order Email',
                'aria-required': 'true',
                'aria-invalid': errors.orderEmail ? 'true' : 'false',
                maxLength: 50
            }
        },
        billingPostcode: {
            name: 'billingPostcode',
            type: 'text',
            autoComplete: 'postal-code',
            rules: {
                required: formatMessage(messages.postcodeRequired),
                minLength: {
                    value: 6,
                    message: formatMessage(messages.postcodeInvalid)
                },
                maxLength: {
                    value: 8,
                    message: formatMessage(messages.postcodeInvalid)
                }
            },
            error: errors.billingPostcode,
            control,
            label: formatMessage(messages.billingPostcodePlaceholder),
            variant: 'floating',
            inputProps: {
                'aria-label': 'Billing Postal Code',
                'aria-required': 'true',
                'aria-invalid': errors.billingPostcode ? 'true' : 'false',
                maxLength: 8
            }
        }
    }

    return fields
}
